// services/probeService.js
import api from "./api";

export const getAllProbes = async (schemaName = null) => {
  try {
    const response = await api.get("/probes/", {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching probes:", error);
    throw error;
  }
};

export const getProbeById = async (probeId, schemaName = null) => {
  try {
    const response = await api.get(`/probes/${probeId}/`, {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching probe:", error);
    throw error;
  }
};

export const updateProbe = async (probeId, probeData, schemaName = null) => {
  try {
    const response = await api.put(`/probes/${probeId}/`, probeData, {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error updating probe:", error);
    throw error;
  }
};

export const createProbe = async (probeData, schemaName = null) => {
  try {
    const response = await api.post("/probes/", probeData, {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error creating probe:", error);
    throw error;
  }
};

export const deleteProbe = async (probeId, schemaName = null) => {
  try {
    const response = await api.delete(`/probes/${probeId}/`, {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting probe:", error);
    throw error;
  }
};

export const getProbesWithParams = async (params = {}) => {
  try {
    const response = await api.get("/probes/", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching probes:", error);
    throw error;
  }
};

// Optional: Add pagination and filtering
export const getProbesWithPagination = async ({
  page = 1,
  pageSize = 10,
  search = "",
  status = "",
  siteId = "",
  ordering = "-created_at",
}) => {
  try {
    const params = new URLSearchParams({
      page,
      page_size: pageSize,
      search,
      ordering,
    });

    if (status) params.append("status", status);
    if (siteId) params.append("site_id", siteId);

    const response = await api.get("/probes/", { params });
    return {
      results: response.data.results,
      count: response.data.count,
      next: response.data.next,
      previous: response.data.previous,
    };
  } catch (error) {
    console.error("Error fetching probes with pagination:", error);
    throw error;
  }
};

// Optional: Add probe status management
export const getProbeStatus = async (probeId) => {
  try {
    const response = await api.get(`/probes/${probeId}/status/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching probe status:", error);
    throw error;
  }
};

export const updateProbeStatus = async (probeId, statusData) => {
  try {
    const response = await api.put(`/probes/${probeId}/status/`, statusData);
    return response.data;
  } catch (error) {
    console.error("Error updating probe status:", error);
    throw error;
  }
};

// Optional: Add probe configuration management
export const getProbeConfig = async (probeId) => {
  try {
    const response = await api.get(`/probes/${probeId}/config/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching probe configuration:", error);
    throw error;
  }
};

export const updateProbeConfig = async (probeId, configData) => {
  try {
    const response = await api.put(`/probes/${probeId}/config/`, configData);
    return response.data;
  } catch (error) {
    console.error("Error updating probe configuration:", error);
    throw error;
  }
};

// Optional: Add bulk operations
export const bulkUpdateProbes = async (probesData) => {
  try {
    const response = await api.put("/probes/bulk-update/", probesData);
    return response.data;
  } catch (error) {
    console.error("Error bulk updating probes:", error);
    throw error;
  }
};

export const bulkDeleteProbes = async (probeIds) => {
  try {
    const response = await api.post("/probes/bulk-delete/", {
      probe_ids: probeIds,
    });
    return response.data;
  } catch (error) {
    console.error("Error bulk deleting probes:", error);
    throw error;
  }
};

// Capture-probe sidecar endpoints. These were dropped when the colleague
// merge took theirs for probeService.js; re-added because AddAlloyDarkstat
// and the WiFi-scan flow still import them. Routes live in
// network/api/urls.py under capture-probes/...

// Returns the probe's ip4_interfaces grain for the interface picker on
// AddAlloyDarkstat / capture forms.
export const getProbeNetworkInterfaces = async (probeId, schemaName = null) => {
  try {
    const response = await api.get(`/capture-probes/${probeId}/interfaces/`, {
      params: schemaName ? { client: schemaName } : {},
    });
    return response.data || {};
  } catch (error) {
    const data = error?.response?.data;
    if (data) return data;
    console.error("Error fetching probe network interfaces:", error);
    throw error;
  }
};

// Triggers a probe-side WiFi scan and returns {networks: [...]} or an error
// payload. Sync salt cmd.run; takes ~5-12s.
export const scanProbeWiFi = async (probeId, schemaName = null) => {
  const response = await api.post(
    `/capture-probes/${probeId}/wifi-scan/`,
    {},
    { params: schemaName ? { client: schemaName } : {} },
  );
  return response.data || {};
};
